import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import ProjectBlogList from '../components/ProjectBlogList';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { StatusLabel } from '../components/StatusLabel';
import { ProjectDTO } from '../DTO/ProjectDTO';
import { DetailSkeleton } from '../components/Skeleton';
import { OptionalImage } from '../components/OptionalImage';
import { assetUrl } from '../config/config';
import { formatPeriod, stripLeadingTitle } from '../lib/format';

const ProjectDetail: React.FC = () => {
    const { slug }               = useParams<{ slug?: string }>();
    const [project, setProject]  = useState<ProjectDTO | null>(null);
    const [loading, setLoading]  = useState(true);
    const [error, setError]      = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const [brokenImages, setBrokenImages]   = useState<Set<string>>(new Set());
    const lastTrigger = useRef<HTMLElement | null>(null);
    const closeButton = useRef<HTMLButtonElement | null>(null);

    const openLightbox  = (i: number, trigger: HTMLElement) => {
        lastTrigger.current = trigger;
        setLightboxIndex(i);
    };
    const closeLightbox = useCallback(() => {
        setLightboxIndex(null);
        lastTrigger.current?.focus();
    }, []);
    const prevImage     = useCallback(() =>
        setLightboxIndex(i => i !== null && i > 0 ? i - 1 : i), []);
    const nextImage     = useCallback((galleryLength: number) =>
        setLightboxIndex(i => i !== null && i < galleryLength - 1 ? i + 1 : i), []);

    useEffect(() => {
        if (!slug) { setLoading(false); return; }
        axios.get<ProjectDTO>(`/api/projects/slug/${slug}`)
            .then(res => setProject(res.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        if (lightboxIndex === null) return;
        closeButton.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape')    closeLightbox();
            if (e.key === 'ArrowLeft') prevImage();
            if (e.key === 'ArrowRight' && project) nextImage(project.gallery.length - brokenImages.size);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxIndex, project, brokenImages, closeLightbox, prevImage, nextImage]);

    if (loading)
        return <DetailSkeleton />;

    if (error || !project)
        return (
            <div className="page-container py-24">
                <h1 className="text-3xl mb-3">Project not found</h1>
                <p className="text-muted mb-6">There is no project at this address. It may have been renamed or unpublished.</p>
                <Link to="/projects" className="link font-label">Back to all projects</Link>
            </div>
        );

    const readme = typeof project.readmeContent === 'string'
        ? stripLeadingTitle(project.readmeContent, project.name)
        : '';
    const period = formatPeriod(project.startDate, project.endDate);
    // Images whose file is missing drop out, so counts and the lightbox stay consistent.
    const gallery = project.gallery.filter(img => !brokenImages.has(img.url));
    const markBroken = (url: string) => setBrokenImages(prev => new Set(prev).add(url));

    return (
        <div>
            {/* ── Header ────────────────────────────────────────────────── */}
            <header className="page-container pt-10 md:pt-14 pb-10">
                <Link to="/projects" className="link font-label inline-flex items-center gap-1 mb-8">
                    <ChevronLeft size={16} aria-hidden />
                    All projects
                </Link>

                <h1 className="text-[clamp(2.25rem,5vw,3.5rem)] leading-[1.05] mb-4">{project.name}</h1>
                <p className="text-muted text-lg max-w-[40rem] m-0">{project.description}</p>
            </header>

            {project.thumbnailUrl && (
                <OptionalImage
                    src={assetUrl(project.thumbnailUrl)}
                    alt={`Screenshot of ${project.name}`}
                    className="w-full max-h-[32rem] object-contain"
                    frame={img => (
                        <div className="page-container pb-12">
                            <div className="overflow-hidden rounded border border-rule bg-surface">{img}</div>
                        </div>
                    )}
                />
            )}

            {/* ── Facts + content ───────────────────────────────────────── */}
            <div className="page-container pb-20 grid gap-10 md:grid-cols-[12rem_1fr] md:gap-12">

                <aside>
                    <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-5 md:grid-cols-1 md:sticky md:top-24">
                        {project.status && (
                            <div>
                                <dt className="label mb-1">Status</dt>
                                <dd className="m-0"><StatusLabel status={project.status} /></dd>
                            </div>
                        )}
                        {period && (
                            <div>
                                <dt className="label mb-1">Period</dt>
                                <dd className="m-0 font-label text-base text-ink">{period}</dd>
                            </div>
                        )}
                        {project.technologies?.length > 0 && (
                            <div className="col-span-2 md:col-span-1">
                                <dt className="label mb-1.5">Stack</dt>
                                <dd className="m-0 flex flex-wrap gap-1.5">
                                    {project.technologies.map(t => <span key={t.id} className="tag">{t.name}</span>)}
                                </dd>
                            </div>
                        )}
                        {(project.projectUrl || project.repoUrl) && (
                            <div className="col-span-2 md:col-span-1">
                                <dt className="label mb-1">Links</dt>
                                <dd className="m-0 flex flex-col gap-1 font-label text-base">
                                    {project.projectUrl && (
                                        <a href={project.projectUrl} target="_blank" rel="noopener noreferrer" className="link self-start">
                                            Live site
                                        </a>
                                    )}
                                    {project.repoUrl && (
                                        <a href={project.repoUrl} target="_blank" rel="noopener noreferrer" className="link self-start">
                                            Source code
                                        </a>
                                    )}
                                </dd>
                            </div>
                        )}
                    </dl>
                </aside>

                <div className="min-w-0 flex flex-col gap-16">
                    {readme && <MarkdownRenderer markdown={readme} />}

                    {gallery.length > 0 && (
                        <section>
                            <h2 className="text-2xl mb-1">Gallery</h2>
                            <p className="label mb-5">
                                {gallery.length} {gallery.length === 1 ? 'image' : 'images'}, select one to enlarge
                            </p>
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                {gallery.map((img, i) => (
                                    <OptionalImage
                                        key={img.url}
                                        src={assetUrl(img.url)}
                                        alt=""
                                        loading="lazy"
                                        className="w-full aspect-[4/3] object-cover"
                                        onError={() => markBroken(img.url)}
                                        frame={thumb => (
                                            <button
                                                onClick={e => openLightbox(i, e.currentTarget)}
                                                className="block overflow-hidden rounded border border-rule bg-surface hover:border-muted transition-colors"
                                                aria-label={`Enlarge image ${i + 1}${img.altText ? `: ${img.altText}` : ''}`}
                                            >
                                                {thumb}
                                            </button>
                                        )}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    <ProjectBlogList projectId={project.id} />
                </div>
            </div>

            {/* ── Lightbox ──────────────────────────────────────────────── */}
            {lightboxIndex !== null && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Image ${lightboxIndex + 1} of ${gallery.length}`}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95"
                    onClick={closeLightbox}
                >
                    <button
                        ref={closeButton}
                        onClick={closeLightbox}
                        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
                        aria-label="Close"
                    >
                        <X size={26} />
                    </button>

                    {lightboxIndex > 0 && (
                        <button
                            onClick={e => { e.stopPropagation(); prevImage(); }}
                            className="absolute left-2 sm:left-4 p-2 text-white/80 hover:text-white"
                            aria-label="Previous image"
                        >
                            <ChevronLeft size={36} />
                        </button>
                    )}

                    <figure className="m-0 flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
                        <img
                            src={assetUrl(gallery[lightboxIndex].url)}
                            alt={gallery[lightboxIndex].altText ?? ''}
                            className="max-w-[88vw] max-h-[80vh] object-contain"
                        />
                        <figcaption className="font-label text-sm text-white/80">
                            {gallery[lightboxIndex].altText && <>{gallery[lightboxIndex].altText} </>}
                            ({lightboxIndex + 1} of {gallery.length})
                        </figcaption>
                    </figure>

                    {lightboxIndex < gallery.length - 1 && (
                        <button
                            onClick={e => { e.stopPropagation(); nextImage(gallery.length); }}
                            className="absolute right-2 sm:right-4 p-2 text-white/80 hover:text-white"
                            aria-label="Next image"
                        >
                            <ChevronRight size={36} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProjectDetail;
